import { SERVICE_URL } from "@app/config/getServiceUrl";
import { type EventContext, captureEventContext } from "@app/config/loggers/analyticsContext";
import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { isExpectedApiError } from "@app/config/sentry";
import { getCookie, isAxiosApiError } from "@base/lib/utils";
import axios, { isAxiosError } from "axios";
import { toEndpointPattern } from "./endpointPattern";

declare module "axios" {
  interface InternalAxiosRequestConfig {
    /** 계측 전용 요청 시작 시각(`performance.now()`). 응답 지연 계산에만 쓴다. */
    requestStartedAt?: number;
    /**
     * 계측 전용. 요청을 **띄운** 화면과 현장.
     *
     * 응답은 화면을 떠난 뒤에 도착할 수 있어서, 전송 시점의 값을 읽으면 실패가 엉뚱한
     * 화면과 현장에 귀속된다. 요청 시작 시점의 값을 들고 다닌다.
     */
    analyticsContext?: EventContext;
  }
}

const CSRF_TOKEN_COOKIE_NAME = "csrf_token";

export const apiClient = axios.create({
  baseURL: `${SERVICE_URL}/ko`,
  withCredentials: true,
  xsrfCookieName: CSRF_TOKEN_COOKIE_NAME,
  xsrfHeaderName: "X-CSRFToken",
});

apiClient.interceptors.request.use(
  (config) => {
    config.requestStartedAt = performance.now();
    config.analyticsContext = captureEventContext();

    const csrfToken = getCookie(CSRF_TOKEN_COOKIE_NAME);
    if (!config.headers["X-Csrftoken"] && csrfToken) {
      config.headers["X-Csrftoken"] = csrfToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    trackApiError(error);
    return Promise.reject(error);
  },
);

/**
 * 실패한 API 호출을 이벤트로 남긴다.
 *
 * Sentry 는 "예상 밖 오류"만 보도록 오프라인·기대 오류를 걸러내고 샘플링까지 걸려 있어서,
 * **실패의 분포**는 지금까지 어디에도 남지 않았다. 현장 회선이 나쁜 앱에서는
 * "어떤 엔드포인트가 어떤 네트워크에서 무너지는가"가 곧 제품 문제이므로 따로 집계한다.
 *
 * 성공 요청은 남기지 않는다. 트래픽 규모가 작아 비율보다 실패의 절대량과 원인 분포가
 * 중요하다고 판단했다. 그 대가로 **에러율(비율)은 계산할 수 없다** — 분모가 없기 때문이다.
 * 나중에 비율이 필요해지면 성공 요청도(전수든 표본이든) 함께 남겨 분모를 만들어야 한다.
 *
 * 네트워크 종류·오프라인 여부는 `EventLogger` 의 공통 프로퍼티로 자동으로 붙는다.
 */
function trackApiError(error: unknown): void {
  try {
    if (!isAxiosError(error)) return;

    // 요청 취소는 실패가 아니다. TanStack Query 의 쿼리 취소·라우트 preload 중단으로
    // 일상적으로 발생하므로, 포함시키면 에러율이 통째로 오염된다.
    if (axios.isCancel(error)) return;

    const { config } = error;
    const startedAt = config?.requestStartedAt;

    EventLogger.track(
      CLIENT_EVENT.API_ERROR,
      {
        endpoint_pattern: toEndpointPattern(config?.url),
        method: config?.method?.toUpperCase() ?? null,
        status: error.response?.status ?? null,
        error_code: isAxiosApiError(error) ? error.response.data.code : null,
        // 응답을 받기까지 걸린 시간. 타임아웃·느린 회선을 구분하는 축이다.
        duration_ms: startedAt === undefined ? null : Math.round(performance.now() - startedAt),
        // 응답 자체가 없는 실패 — 회선 단절과 서버 오류를 가른다.
        is_network_error: error.response === undefined,
        is_timeout: error.code === "ECONNABORTED" || error.code === "ETIMEDOUT",
        // Sentry 가 의도적으로 버리는 "예상된" 오류. 집계에서 빼고 볼 수 있게 표시만 한다.
        is_expected: isExpectedApiError(error),
      },
      { context: config?.analyticsContext },
    );
  } catch {
    // 계측이 요청 실패 처리 흐름을 바꾸지 못하게 격리한다.
  }
}
